import { Components, Theme } from '@mui/material'
import { focusRing } from './focusRing'
import { primaryContainedButtonStyle, secondaryContainedButtonStyle } from './MuiButton'

// Attempt to identify the common "last button is icon button" pattern
const lastButtonWithIconSelector = '& .MuiButtonGroup-lastButton:has(> svg:first-child:last-child)'

export const MuiButtonGroup: Components<Omit<Theme, 'components'>>['MuiButtonGroup'] = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    variant: 'contained',
  },
  styleOverrides: {
    grouped: ({ theme }) => ({
      '&.Mui-focusVisible': focusRing(theme, { inset: true }),
    }),
  },
  variants: [
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => ({
        // No backdrop on the group: a disabled button dims with opacity, and a brand fill behind it would show
        // through and leave it looking enabled.
        '& .MuiButton-root': primaryContainedButtonStyle(theme),
        '& .MuiButtonGroup-firstButton, & .MuiButtonGroup-middleButton': {
          borderRightWidth: theme.strokeWidth.default,
          borderRightColor: theme.palette.border.transparent,
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: ({ theme }) => ({
        // Don't leak the zIndex stuff from here
        isolation: 'isolate',
        '& .MuiButton-root': secondaryContainedButtonStyle(theme),
        // Each button draws its own inset border, so MUI's real divider is redundant
        '& .MuiButtonGroup-firstButton, & .MuiButtonGroup-middleButton': {
          borderRight: 'none',
          '&.Mui-disabled': {
            borderRight: 'none',
          },
        },
        '& .MuiButton-root:not(.MuiButtonGroup-firstButton)': {
          // Overlap "borders", so a seam is one stroke wide rather than two
          marginLeft: `-${theme.strokeWidth.default}`,
        },
        // Bring later ones forward when hovered or pressed so their border isn't hidden by the next one
        '& .MuiButton-root:hover': {
          zIndex: 1,
        },
        '& .MuiButton-root:active, & .MuiButton-root.Mui-focusVisible': {
          zIndex: 2,
        },
      }),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        [lastButtonWithIconSelector]: {
          padding: theme.spacing(2.5),
        },
      }),
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-lg'],
        [lastButtonWithIconSelector]: {
          padding: theme.spacing(2),
        },
      }),
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-md'],
        [lastButtonWithIconSelector]: {
          padding: theme.spacing(1),
          minWidth: 'unset',
        },
      }),
    },
  ],
}
